import luxon, { DateTime } from "luxon";
import { RRule } from "rrule";
import { Constants } from "../../../../common/constants";
import {
	SerializedEvent,
	EventCreationFieldValues,
	EventCreationProps,
	EventCreationState,
} from "../../../../common/sesh_types";
import { time_format_utils } from "../../../../common/time_format_utils";
import { EventCreation, EventCreationInputKeys } from "../event_creation";

/**
 * Map of guild default setting to event state key
 */
export const GuildSettingToEventSetting = {
	attendee_role_data: "attendee_role_data",
	default_event_embed_color: "color",
	default_event_server_reminders: "server_reminders",
	default_event_thread_settings: "thread_settings",
	default_hide_attendees: "hide_attendees",
	default_native_event_data: "native_event_data",
	signup_options: "rsvp_options",
	default_role_mentions_on_event_create: "role_mentions_on_create",
	default_user_mentions_on_event_create: "user_mentions_on_create",
} satisfies Record<keyof GuildDefaultsWithoutTimezone, EventCreationInputKeys>;

export const EventSettingToGuildSetting = Object.fromEntries(
	Object.entries(GuildSettingToEventSetting).map(([key, value]) => [
		value,
		key,
	])
) as Record<EventCreationInputKeys, keyof GuildDefaultsWithoutTimezone>;

export const ServerRemindersGroupedKeys: EventCreationInputKeys[] = [
	"server_reminders",
	"role_mentions_on_create",
	"user_mentions_on_create",
];

export type GuildDefaultsWithoutTimezone = Omit<
	EventCreationProps["guild_defaults"],
	"timezone"
>;
export type ModifiableGuildDefaultsKeys =
	(typeof GuildSettingToEventSetting)[keyof GuildDefaultsWithoutTimezone];

export const DefaultGuildSettingValues: Partial<
	EventCreationProps["guild_defaults"]
> = {
	signup_options: Constants.WEB_UI_DEFAULT_REACTION_PRESETS[0],
	default_hide_attendees: false,
	default_event_embed_color: Constants.EMBED_COLOR,
};

type EventEditableStateKeys = keyof Omit<
	SerializedEvent,
	"author_id" | "event_message_id"
>;

/**
 * Map of EventEditableStateKeys (sent from backend) to EventCreationState keys (used on frontend)
 */
const EventEditableDataKeyToStateKey = {
	start_time: "start_time",
	hide_attendees: "hide_attendees",
	allow_maintain_repeat_rsvp: "allow_maintain_repeat_rsvp",
	attendee_role_data: "attendee_role_data",
	thread_settings: "thread_settings",
	color: "color",
	description: "description",
	eligible_role_ids: "eligible_role_ids",
	duration_ms: "duration_ms",
	event_name: "event_name",
	event_timezone: "event_timezone",
	role_mentions_on_create: "role_mentions_on_create",
	user_mentions_on_create: "user_mentions_on_create",
	image_url: "image_url",
	location: "location",
	native_event_data: "native_event_data",
	rrule_str: "rrule",
	rsvp_options: "rsvp_options",
	sent_channel_data: "selected_channel",
	server_reminders: "server_reminders",
	exdates: "exdates",
	rdates: "rdates",
	rsvp_lock_before_ms: "rsvp_lock_before_ms",
} satisfies Record<EventEditableStateKeys, keyof EventCreationFieldValues>;

type EventEditDataTransformer = {
	[K in EventEditableStateKeys]:
		| ((
				ctx: { user_timezone: string; start_time: DateTime },
				val: SerializedEvent[K]
		  ) => EventCreationState[(typeof EventEditableDataKeyToStateKey)[K]])
		| undefined;
};

/**
 * Key is EventCreationEditData key
 * Transforms data received from backend to data that can be used in the frontend
 * If undefined, then we do not need to convert the data
 */
export const EventEditDataToStateData: EventEditDataTransformer = {
	start_time: (ctx, raw_date) =>
		DateTime.fromJSDate(new Date(raw_date)).toUTC(),
	rrule_str: (ctx, rrule_str) => {
		if (!rrule_str) return;
		return new RRule(
			time_format_utils.transformRRuleToTimezone(
				RRule.parseString(rrule_str),
				ctx.user_timezone,
				ctx.start_time,
				true
			)
		);
	},
	exdates: (ctx, exdates) => {
		if (!exdates) return;
		return exdates.map((val) => new Date(val));
	},

	duration_ms: undefined,
	sent_channel_data: undefined,
	eligible_role_ids: undefined,
	allow_maintain_repeat_rsvp: undefined,
	attendee_role_data: undefined,
	color: (ctx, color) => color ?? Constants.EMBED_COLOR,
	description: undefined,
	event_name: undefined,
	event_timezone: undefined,
	hide_attendees: undefined,
	image_url: undefined,
	location: undefined,
	native_event_data: undefined,
	role_mentions_on_create: undefined,
	rsvp_options: undefined,
	thread_settings: undefined,
	user_mentions_on_create: undefined,
	server_reminders: undefined,
	rdates: undefined,
	rsvp_lock_before_ms: undefined,
} as const;

export function createInitialEventState(
	event: EventCreation,
	ctx: { user_timezone: string }
): EventCreationFieldValues {
	// EDIT
	const result: Partial<EventCreationFieldValues> = {};

	const event_data = event.props.is_edit
		? event.props.edit_data
		: event.props.template_data?.json;
	if (event_data) {
		const start_time = EventEditDataToStateData.start_time(
			{ ...ctx, start_time: undefined },
			event_data.start_time
		);

		for (const key in event_data) {
			const edit_key = key as keyof SerializedEvent;
			const state_key = EventEditableDataKeyToStateKey[edit_key];

			result[state_key] =
				EventEditDataToStateData[edit_key]?.(
					{ ...ctx, start_time },
					event_data[edit_key]
				) ?? event_data[edit_key];
		}

		const channel_data = event.getUpdatedChannelData();

		if (event.props.template_data) {
			result.selected_channel = channel_data?.selected_channel
				? {
						id: channel_data?.selected_channel?.id,
						forum_tag_ids: [],
				  }
				: undefined;
		}

		return result as EventCreationFieldValues;
	}
	// END EDIT

	const channel_data = event.getUpdatedChannelData();

	const guild_default_values: Partial<
		Pick<EventCreationFieldValues, ModifiableGuildDefaultsKeys>
	> = {};
	for (const guild_key in GuildSettingToEventSetting) {
		const casted_guild_key =
			guild_key as keyof GuildDefaultsWithoutTimezone;

		const event_key = GuildSettingToEventSetting[
			casted_guild_key
		] as keyof EventCreationFieldValues;

		(guild_default_values[event_key] as any) =
			event.getGuildOrChannelSettingWithChannelParam(
				casted_guild_key,
				channel_data?.selected_channel
			);
	}

	const complete_guild_default_values = guild_default_values as Pick<
		EventCreationFieldValues,
		ModifiableGuildDefaultsKeys
	>;

	const params = new URLSearchParams(event.props.location.search);

	const start_time_from_state = params.get("date") ?? undefined;
	const event_title_from_state = params.get("title") ?? undefined;
	const event_description_from_state = params.get("description") ?? undefined;

	// TODO: should ideally remove date from URL but no big deal

	const other: Omit<EventCreationFieldValues, ModifiableGuildDefaultsKeys> = {
		event_name: event_title_from_state ?? "",
		description: event_description_from_state ?? "",
		// from calendar page
		start_time: start_time_from_state
			? DateTime.fromJSDate(
					new Date(decodeURIComponent(start_time_from_state))
			  )
			: undefined,
		exdates: undefined,
		eligible_role_ids: [],
		duration_ms: undefined,
		rsvp_lock_before_ms: null,
		event_timezone: ctx.user_timezone,
		image_url: undefined,
		rrule: undefined,
		selected_channel: channel_data?.selected_channel
			? {
					id: channel_data?.selected_channel?.id,
					forum_tag_ids: [],
			  }
			: undefined,
		allow_maintain_repeat_rsvp: true,
		rdates: undefined,
		location: null,
	};

	return {
		...complete_guild_default_values,
		...other,
	};
}
